/**
 * MediaPipe BlazePose integration for pose extraction from video
 * Ported from TypeScript to JavaScript
 */

import {
  FilesetResolver,
  PoseLandmarker,
  DrawingUtils,
} from '@mediapipe/tasks-vision';

export class PoseExtractor {
  constructor() {
    this.landmarker = null;
    this.initialized = false;
    this.lastTimestamp = 0;
  }

  /**
   * Initialize the MediaPipe PoseLandmarker
   */
  async init() {
    if (this.initialized) {
      return;
    }

    try {
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
      );

      this.landmarker = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath:
            'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/latest/pose_landmarker_full.task',
          delegate: 'GPU',
        },
        runningMode: 'VIDEO',
        numPoses: 1,
        minPoseDetectionConfidence: 0.5,
        minPosePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
        outputSegmentationMasks: false,
      });

      this.initialized = true;
      console.log('✅ MediaPipe PoseLandmarker initialized');
    } catch (error) {
      console.error('Failed to initialize PoseLandmarker:', error);
      throw error;
    }
  }

  /**
   * Check if the extractor is initialized
   * @returns {boolean}
   */
  isInitialized() {
    return this.initialized;
  }

  /**
   * Extract pose frames from a video element by playing it
   * @param {HTMLVideoElement} video - HTML Video Element
   * @param {number} sampleRateHz - Frames per second to sample (default: 30)
   * @param {function} onProgress - Optional callback for progress updates
   * @returns {Promise<Array>} - Array of pose frames
   */
  async extractFromVideo(video, sampleRateHz = 30, onProgress = null) {
    if (!this.landmarker) {
      throw new Error('PoseExtractor not initialized. Call init() first.');
    }

    const frames = [];
    const duration = video.duration;

    if (!duration || duration === Infinity) {
      throw new Error('Video duration is invalid or not loaded');
    }

    console.log(`🎬 Extracting poses from video (${duration.toFixed(2)}s) using playback method`);
    console.log(`Video dimensions: ${video.videoWidth}x${video.videoHeight}, readyState: ${video.readyState}`);

    return new Promise((resolve, reject) => {
      let lastCaptureTime = -1;
      let frameCount = 0;
      const captureInterval = 1 / sampleRateHz;

      const processFrame = () => {
        if (video.paused || video.ended) {
          return;
        }

        const currentTime = video.currentTime;

        if (currentTime - lastCaptureTime >= captureInterval) {
          lastCaptureTime = currentTime;
          frameCount++;

          this.lastTimestamp += 33;
          const timestampMs = this.lastTimestamp;

          try {
            if (video.readyState < 2 || video.videoWidth === 0) {
              console.warn(`Video not ready at frame ${frameCount}`);
              requestAnimationFrame(processFrame);
              return;
            }

            const result = this.landmarker.detectForVideo(video, timestampMs);

            if (frameCount <= 3 || frameCount % 30 === 0) {
              console.log(`Frame ${frameCount}: landmarks=${result.landmarks?.length || 0}`);
            }

            const frame = this.convertResultToFrame(result, timestampMs);

            if (frame) {
              frames.push(frame);
            }

            if (onProgress) {
              const progress = Math.min((currentTime / duration) * 100, 100);
              onProgress(progress);
            }
          } catch (error) {
            console.warn(`Error detecting pose at time ${currentTime.toFixed(2)}s:`, error);
          }
        }

        requestAnimationFrame(processFrame);
      };

      const onEnded = () => {
        video.removeEventListener('ended', onEnded);
        video.removeEventListener('error', onError);
        console.log(`✅ Extracted ${frames.length} pose frames`);
        video.pause();
        video.currentTime = 0;
        resolve(frames);
      };

      const onError = () => {
        video.removeEventListener('ended', onEnded);
        video.removeEventListener('error', onError);
        reject(new Error('Video playback error during pose extraction'));
      };

      video.addEventListener('ended', onEnded);
      video.addEventListener('error', onError);

      video.currentTime = 0;
      video.play().then(() => {
        processFrame();
      }).catch(reject);
    });
  }

  /**
   * Detect pose for a single frame (synchronous, for frame extraction)
   * @param {HTMLVideoElement} video - Video element
   * @param {number} timestampMs - Timestamp in milliseconds
   * @returns {Object|null} - Pose result or null
   */
  detectForFrame(video, timestampMs) {
    if (!this.landmarker) {
      throw new Error('PoseExtractor not initialized');
    }

    this.lastTimestamp = Math.max(this.lastTimestamp, timestampMs);
    return this.landmarker.detectForVideo(video, this.lastTimestamp);
  }

  /**
   * Real-time pose detection with callback
   * @param {HTMLVideoElement} video - Video element
   * @param {number} timestampMs - Timestamp
   * @param {function} callback - Callback with result
   */
  detectForVideoCallback(video, timestampMs, callback) {
    if (!this.landmarker) {
      throw new Error('PoseExtractor not initialized');
    }

    this.lastTimestamp = Math.max(this.lastTimestamp, timestampMs);
    this.landmarker.detectForVideo(video, timestampMs, callback);
  }

  /**
   * Convert MediaPipe result to our PoseFrame format
   * @param {Object} result - MediaPipe result
   * @param {number} timestampMs - Timestamp
   * @returns {Object|null} - Pose frame or null
   */
  convertResultToFrame(result, timestampMs) {
    const landmarks = result?.landmarks?.[0];

    if (!landmarks || landmarks.length === 0) {
      return null;
    }

    const convertedLandmarks = landmarks.map((lm) => ({
      x: lm.x,
      y: lm.y,
      z: lm.z ?? 0,
      visibility: lm.visibility,
    }));

    return {
      timestampMs,
      landmarks: convertedLandmarks,
    };
  }

  /**
   * Draw pose landmarks using MediaPipe's DrawingUtils
   * @param {HTMLCanvasElement} canvas - Canvas element
   * @param {Object} result - MediaPipe result
   */
  drawPoseResults(canvas, result) {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.save();
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const drawingUtils = new DrawingUtils(ctx);

    for (const landmarks of result.landmarks) {
      // Draw connections (green lines)
      drawingUtils.drawConnectors(
        landmarks,
        PoseLandmarker.POSE_CONNECTIONS,
        { color: '#4ade80', lineWidth: 3 }
      );

      // Draw landmarks (blue dots with depth-based sizing)
      drawingUtils.drawLandmarks(landmarks, {
        color: '#818cf8',
        lineWidth: 2,
        radius: (data) => DrawingUtils.lerp(data.from?.z || 0, -0.15, 0.1, 6, 2)
      });
    }

    ctx.restore();
  }

  /**
   * Draw pose on a canvas context (without clearing - for compositing)
   * @param {CanvasRenderingContext2D} ctx - Canvas context
   * @param {Object} result - MediaPipe result
   * @param {number} width - Canvas width
   * @param {number} height - Canvas height
   */
  drawPoseOnContext(ctx, result, width, height) {
    if (!result?.landmarks?.length) return;

    const drawingUtils = new DrawingUtils(ctx);

    for (const landmarks of result.landmarks) {
      drawingUtils.drawConnectors(
        landmarks,
        PoseLandmarker.POSE_CONNECTIONS,
        { color: '#4ade80', lineWidth: 3 }
      );

      drawingUtils.drawLandmarks(landmarks, {
        color: '#818cf8',
        lineWidth: 2,
        radius: (data) => DrawingUtils.lerp(data.from?.z || 0, -0.15, 0.1, 6, 2)
      });
    }
  }

  /**
   * Reset timestamp tracking
   */
  resetTimestamp() {
    this.lastTimestamp = 0;
  }

  /**
   * Clean up resources
   */
  dispose() {
    if (this.landmarker) {
      this.landmarker.close();
      this.landmarker = null;
      this.initialized = false;
      this.lastTimestamp = 0;
    }
  }
}

// Singleton instance for shared use
let sharedInstance = null;

/**
 * Get or create a shared PoseExtractor instance
 * @returns {Promise<PoseExtractor>}
 */
export async function getSharedPoseExtractor() {
  if (!sharedInstance) {
    sharedInstance = new PoseExtractor();
    await sharedInstance.init();
  }
  return sharedInstance;
}

/**
 * Dispose the shared instance
 */
export function disposeSharedPoseExtractor() {
  if (sharedInstance) {
    sharedInstance.dispose();
    sharedInstance = null;
  }
}
